import {
  Button,
  DialogFooter,
  LoadingButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@fortem-monorepo/react-ui'
import type { AccruedInterests } from '@fortem-monorepo/web-sdk'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { isFutureBalanceDate } from '@/lib/accrued-interest-dates'
import { useConfirmAccruedInterestBalances } from '@/react-query/mutations/accrued-interests'

import { useAccruedInterestsContext } from '../AccruedInterests.context'
import { type BalanceDisplayRow, toConfirmationRows } from '../balance-drafts'
import { AccruedInterestDialogFrame } from './AccruedInterestDialogFrame'

export type ConfirmBalancesDialogProps = {
  params: AccruedInterests.Params
  rows: BalanceDisplayRow[]
  onClose: () => void
}

export const ConfirmBalancesDialog = ({ params, rows, onClose }: ConfirmBalancesDialogProps) => {
  const mutation = useConfirmAccruedInterestBalances()
  const { clearDrafts } = useAccruedInterestsContext()
  const confirm = async () => {
    if (!rows.length || mutation.isPending || isFutureBalanceDate(params.value_date)) return
    try {
      await mutation.mutateAsync({ ...params, rows: toConfirmationRows(rows) })
      clearDrafts(
        params,
        rows.map((row) => row.trade_id),
      )
      onClose()
    } catch {
      // Keep the frozen selection and drafts available after failure.
    }
  }
  return (
    <AccruedInterestDialogFrame
      title="Confirm displayed balances"
      description={`${rows.length} balances · ${params.fund_name} · ${params.value_date}`}
      pending={mutation.isPending}
      onClose={onClose}
    >
      <p className="text-sm text-muted-foreground">
        Only the displayed trades below will be saved. Pending corrections become manual balances.
      </p>
      <div className="max-h-96 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Trade</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Opening</TableHead>
              <TableHead>Daily</TableHead>
              <TableHead>Closing</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.trade_id}>
                <TableCell>{row.trade_id}</TableCell>
                <TableCell>{row.currency ?? '—'}</TableCell>
                <TableCell>{row.opening_balance_local ?? '—'}</TableCell>
                <TableCell>{row.trade_daily_accrual_amount ?? '—'}</TableCell>
                <TableCell>{row.accrued_balance_local ?? '—'}</TableCell>
                <TableCell>{row.notes ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.some((row) => row.stale) && (
        <p role="alert" className="text-sm text-destructive">
          Some pending corrections use older source values. Review them again or discard and refresh before confirming.
        </p>
      )}
      {mutation.isError && (
        <p role="alert" className="text-sm text-destructive">
          {getErrorMessage(mutation.error)}
        </p>
      )}
      <DialogFooter>
        <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={onClose}>
          Back
        </Button>
        <LoadingButton
          type="button"
          size="sm"
          disabled={mutation.isPending || isFutureBalanceDate(params.value_date) || rows.some((row) => row.stale)}
          onClick={confirm}
        >
          Confirm {rows.length} balances
        </LoadingButton>
      </DialogFooter>
    </AccruedInterestDialogFrame>
  )
}
